import IPoll, { PollEventTypeCode, PollType } from '../model/IPoll';
import { session, apiBaseUrl } from '../auth/session';

/**
 * Poll API client.
 *
 * Ported from the SPFx service of the same name. The only thing that changed is
 * the transport: `AadHttpClient` (an Entra ID token minted by SharePoint) is
 * replaced by `fetch` carrying this app's own JWT. Every request goes to the
 * local gateway under /api, which verifies the JWT and forwards the call to
 * API_BASE_URL. All request/response shapes are unchanged.
 */

const SURVEY_BASE_URL = (import.meta.env.VITE_SURVEY_BASE_URL ?? '').replace(/\/+$/, '');

/** Set by the gateway when it — not the upstream API — rejected the token. */
const GATEWAY_AUTH_HEADER = 'X-Gateway-Auth';

export class HttpError extends Error {
    public readonly status: number;

    public constructor(message: string, status: number) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
    }
}

export default class DataService implements IDataService {
    private readonly _baseUrl: string = `${apiBaseUrl}/api`;
    private readonly _surveyBaseUrl: string = SURVEY_BASE_URL;

    public getSurveyUrl(slug?: string): string {
        if (!slug) return '';
        return `${this._surveyBaseUrl}/survey/${encodeURIComponent(slug)}`;
    }

    public async getPolls(request: PagedListRequest): Promise<unknown> {
        const url: string = `${this._baseUrl}/polls/list`;
        return await this.post<unknown>(url, JSON.stringify(request), data => {
            return data;
        });
    }

    public async createPoll(request: ICreatePollRequest): Promise<IPoll> {
        const url: string = `${this._baseUrl}/polls`;
        return await this.post<IPoll>(url, JSON.stringify(request), data => data?.value ?? data);
    }

    public async getPoll(pollId: string): Promise<IPoll> {
        const url: string = `${this._baseUrl}/polls/${encodeURIComponent(pollId)}`;
        return await this.get<IPoll>(url, data => data?.value ?? data);
    }

    public async updatePoll(pollId: string, request: IUpdatePollRequest): Promise<IPoll> {
        const url: string = `${this._baseUrl}/polls/${encodeURIComponent(pollId)}`;
        return await this.put<IPoll>(url, JSON.stringify(request), data => data?.value ?? data);
    }

    public async getGeoRegions(): Promise<IGeoRegion[]> {
        const url: string = `${this._baseUrl}/georegions`;
        return await this.get<IGeoRegion[]>(url, data => data?.regions ?? []);
    }

    public async getTerritoriesByRegion(regionId: string): Promise<IGeoTerritory[]> {
        const url: string = `${this._baseUrl}/georegions/${encodeURIComponent(regionId)}/territories`;
        return await this.get<IGeoTerritory[]>(url, data => data?.territories ?? []);
    }

    public async updateBranding(id: string, request: IUpdatePollBrandingRequest): Promise<void> {
        const url: string = `${this._baseUrl}/polls/${encodeURIComponent(id)}/branding`;
        await this.post<void>(url, JSON.stringify(request), () => undefined);
    }

    public async getPollResults(pollId: string): Promise<IPollResultsResponse> {
        const url: string = `${this._baseUrl}/presentation/polls/${encodeURIComponent(pollId)}/statistics`;
        return await this.get<IPollResultsResponse>(url, data => data?.value ?? data);
    }

    public async getTemplates(): Promise<IPollTemplate[]> {
        const url: string = `${this._baseUrl}/templates`;
        return await this.get<IPollTemplate[]>(url, data => Array.isArray(data) ? data : (data?.templates ?? []));
    }

    public async getTemplateById(id: string): Promise<IPollTemplate> {
        const url: string = `${this._baseUrl}/templates/${encodeURIComponent(id)}`;
        return await this.get<IPollTemplate>(url, data => data?.value ?? data);
    }

    public async createTemplate(request: ICreatePollTemplateRequest): Promise<IPollTemplate> {
        const url: string = `${this._baseUrl}/templates`;
        return await this.post<IPollTemplate>(url, JSON.stringify(request), data => data?.value ?? data);
    }

    public async updateTemplate(id: string, request: IUpdatePollTemplateRequest): Promise<IPollTemplate> {
        const url: string = `${this._baseUrl}/templates/${encodeURIComponent(id)}`;
        return await this.put<IPollTemplate>(url, JSON.stringify(request), data => data?.value ?? data);
    }

    public async deleteTemplate(id: string): Promise<void> {
        const url: string = `${this._baseUrl}/templates/${encodeURIComponent(id)}`;
        await this.delete(url);
    }

    public async deletePoll(pollId: string): Promise<void> {
        const url: string = `${this._baseUrl}/polls/${encodeURIComponent(pollId)}`;
        await this.delete(url);
    }

    // --- transport ---------------------------------------------------------

    /**
     * Sends the request with the current access token. On a 401 it refreshes
     * once and replays, so an expired token is invisible to the caller.
     *
     * Only the gateway's own 401 triggers that. A 401 relayed from the upstream
     * API means *its* credentials were rejected, not ours — refreshing would be
     * pointless, and a failed refresh would sign the user out of a perfectly
     * valid session.
     */
    private async request(url: string, init: RequestInit, isRetry = false): Promise<Response> {
        const token = session.getAccessToken();
        const headers = new Headers(init.headers);
        headers.set('Accept', 'application/json');
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }

        const response = await fetch(url, { ...init, headers, credentials: 'include' });

        const isGatewayAuthFailure = response.status === 401
            && response.headers.get(GATEWAY_AUTH_HEADER) === 'failed';

        if (isGatewayAuthFailure && !isRetry) {
            const refreshed = await session.refresh();
            if (refreshed) {
                return this.request(url, init, true);
            }
        }

        if (!response.ok) {
            throw new HttpError(await this.describeFailure(response), response.status);
        }

        return response;
    }

    private async describeFailure(response: Response): Promise<string> {
        try {
            const text = await response.text();
            if (!text) {
                return `Запрос завершился с кодом ${response.status}.`;
            }

            try {
                const body = JSON.parse(text);
                return body?.message ?? body?.Message ?? body?.error ?? text;
            } catch {
                return text;
            }
        } catch {
            return `Запрос завершился с кодом ${response.status}.`;
        }
    }

    /** Parses a JSON body, tolerating the empty responses the API returns for writes. */
    private async parse(response: Response): Promise<any> {
        if (response.status === 204) {
            return undefined;
        }

        const text = await response.text();
        if (!text) {
            return undefined;
        }

        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    }

    private async get<T>(url: string, func: (data: any) => T): Promise<T> {
        const response = await this.request(url, { method: 'GET' });
        return func(await this.parse(response));
    }

    private async post<T>(url: string, body: string, func: (data: any) => T): Promise<T> {
        const response = await this.request(url, {
            method: 'POST',
            body,
            headers: { 'Content-Type': 'application/json' },
        });
        return func(await this.parse(response));
    }

    private async put<T>(url: string, body: string, func: (data: any) => T): Promise<T> {
        const response = await this.request(url, {
            method: 'PUT',
            body,
            headers: { 'Content-Type': 'application/json' },
        });
        return func(await this.parse(response));
    }

    private async delete(url: string): Promise<void> {
        await this.request(url, {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
        });
    }
}


export interface IDataService {

    getSurveyUrl(slug?: string): string;

    getPolls(request: PagedListRequest): Promise<unknown>;

    createPoll(request: ICreatePollRequest): Promise<IPoll>;

    getPoll(pollId: string): Promise<IPoll>;

    updatePoll(pollId: string, request: IUpdatePollRequest): Promise<IPoll>;

    getGeoRegions(): Promise<IGeoRegion[]>;

    getTerritoriesByRegion(regionId: string): Promise<IGeoTerritory[]>;

    updateBranding(id: string, request: IUpdatePollBrandingRequest): Promise<void>;

    getPollResults(pollId: string): Promise<IPollResultsResponse>;

    getTemplates(): Promise<IPollTemplate[]>;

    getTemplateById(id: string): Promise<IPollTemplate>;

    createTemplate(request: ICreatePollTemplateRequest): Promise<IPollTemplate>;

    updateTemplate(id: string, request: IUpdatePollTemplateRequest): Promise<IPollTemplate>;

    deleteTemplate(id: string): Promise<void>;

    deletePoll(pollId: string): Promise<void>;

}

export interface IPollResultsChoice {
    choiceId?: string;
    choiceText?: string;
    count: number;
    percentage: number;
}

export interface IPollResultsWord {
    text: string;
    count: number;
    weight: number;
}

export interface IPollResultsQuestion {
    questionId: string;
    questionText: string;
    questionType: number;
    totalAnswers: number;
    average?: number;
    min?: number;
    max?: number;
    choices?: IPollResultsChoice[];
    textAnswers?: string[];
    words?: IPollResultsWord[];
}

export interface IPollResultsResponse {
    pollId?: string;
    hasResults: boolean;
    totalResponses: number;
    questions: IPollResultsQuestion[];
}

export interface IUpdatePollBrandingRequest {
    BrandingBackgroundType?: number;
    BrandingBackgroundColor?: string | null;
    BrandingBackgroundGradient?: string | null;
    BrandingLogoUrl?: string | null;
    BrandingLogoPositionCode?: number | null;
    BrandingLogoSize?: number | null;
    QrcodeVisible?: boolean;
    QrcodePositionCode?: number;
    QrcodeForeground?: string | null;
    QrcodeBackground?: string | null;
}

export interface IGeoRegion {
    Id: string;
    Name: string;
    territoriesCount?: number;
}

export interface IGeoTerritory {
    Id: string;
    Name: string;
    FederalSubject?: string;
    City?: string;
    RegionId?: string;
    regionName?: string;
}

export interface ICreatePollQuestionOption {
    Text: string;
    Order: number;
}

export enum PollState {
    Draft = 0,
    Review = 1,
    Rejected = 2,
    Approved = 3,
    Scheduled = 4,
    Active = 5,
    Paused = 6,
    Closed = 7,
    Archived = 8,
    Deleted = 9
}

export enum PollQuestionType {
    Rating = 1,
    SingleChoice = 2,
    MultipleChoice = 3,
    Text = 4,
    Nps = 5,
    WordCloud = 6
}

export interface ICreatePollQuestion {
    Type: PollQuestionType;
    Title: string;
    Required: boolean;
    Options?: ICreatePollQuestionOption[];
    MinValue?: number;
    MaxValue?: number;
    Placeholder?: string;
}

export interface IUpdatePollQuestionOption {
    Id?: string;
    Text: string;
    Order: number;
}

export interface IUpdatePollQuestion {
    Id?: string;
    Type: PollQuestionType;
    Title: string;
    Required: boolean;
    Options?: IUpdatePollQuestionOption[];
    MinValue?: number;
    MaxValue?: number;
    Placeholder?: string;
}

export interface ICreatePollRequest {
    Quick: boolean;
    Type: PollType;
    Title: string;
    Description?: string;
    EventTypeCode?: PollEventTypeCode;
    IsAnonymous: boolean;
    GeoRegionId?: string;
    GeoTerritoryId?: string;
    SingleResponse: boolean;
    OpensAt?: string;
    ClosesAt?: string;
    CompletedDisplayResults: boolean;
    CompletedMessage?: string;
    Questions: ICreatePollQuestion[];
}

export interface IUpdatePollRequest {
    Title: string;
    Description?: string;
    EventTypeCode?: PollEventTypeCode;
    GeoRegionId?: string;
    GeoTerritoryId?: string;
    SingleResponse: boolean;
    OpensAt?: string;
    ClosesAt?: string;
    CompletedDisplayResults: boolean;
    CompletedMessage?: string;
    Questions?: IUpdatePollQuestion[];
}

export interface FilterDefinition {
    Field: string;
    Value: unknown;
}

export interface ExportListRequest {
    SearchQuery?: string;
    SortColumn?: string;
    SortDirection?: string;
    Status?: string;
    Filters?: FilterDefinition[];
}

export interface PagedListRequest extends ExportListRequest {
    PageIndex?: number;
    PageSize?: number;
}

export interface IPollTemplateChoice {
    Id: string;
    TemplateId: string;
    Text: string;
    Order: number;
    Placeholder?: string;
}

export interface IPollTemplate {
    Id: string;
    Order: number;
    Text: string;
    TypeCode: number;
    Choices: IPollTemplateChoice[];
}

export interface ICreatePollTemplateChoiceRequest {
    Text: string;
    Order: number;
    Placeholder?: string;
}

export interface ICreatePollTemplateRequest {
    Order: number;
    Text: string;
    TypeCode: number;
    Choices?: ICreatePollTemplateChoiceRequest[];
}

export interface IUpdatePollTemplateChoiceRequest {
    Id?: string;
    Text: string;
    Order: number;
    Placeholder?: string;
}

export interface IUpdatePollTemplateRequest {
    Order: number;
    Text: string;
    TypeCode: number;
    Choices?: IUpdatePollTemplateChoiceRequest[];
}
